import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";

const API_URL = import.meta.env.VITE_API_URL;
const TOKEN_KEY = "peart_auth_token";
const TOKEN_EXPIRY_KEY = "peart_auth_token_expiry";
const LOGIN_ATTEMPTS_KEY = "peart_login_attempts";
const MAX_LOGIN_ATTEMPTS = 5;
const LOCKOUT_DURATION = 15 * 60 * 1000; // 15 minutes in milliseconds
const TOKEN_EXPIRY = 4 * 60 * 60 * 1000; // 4 hours in milliseconds

function Login() {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // Check token expiry on component mount
    const token = localStorage.getItem(TOKEN_KEY);
    const tokenExpiry = localStorage.getItem(TOKEN_EXPIRY_KEY);
    
    if (token && tokenExpiry) {
      const now = Date.now();
      if (now > parseInt(tokenExpiry)) {
        // Token expired, clear it
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(TOKEN_EXPIRY_KEY);
      } else if (location.pathname !== "/login") {
        window.location.href = "/admin";
      }
    }
  }, [location]);

  const checkLoginAttempts = () => {
    const attempts = JSON.parse(localStorage.getItem(LOGIN_ATTEMPTS_KEY) || "[]");
    const now = Date.now();
    
    // Remove expired attempts
    const validAttempts = attempts.filter(attempt => 
      now - attempt.timestamp < LOCKOUT_DURATION
    );
    
    if (validAttempts.length >= MAX_LOGIN_ATTEMPTS) {
      const oldestAttempt = validAttempts[0];
      const timeLeft = LOCKOUT_DURATION - (now - oldestAttempt.timestamp);
      const minutesLeft = Math.ceil(timeLeft / (60 * 1000));
      throw new Error(`Zu viele Anmeldeversuche. Bitte warten Sie ${minutesLeft} Minuten.`);
    }
    
    return validAttempts;
  };

  const recordLoginAttempt = (success) => {
    const attempts = checkLoginAttempts();
    attempts.push({
      timestamp: Date.now(),
      success
    });
    localStorage.setItem(LOGIN_ATTEMPTS_KEY, JSON.stringify(attempts));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    try {
      // Check login attempts before proceeding
      checkLoginAttempts();

      const formData = new FormData();
      formData.append("user", username);
      formData.append("pass", password);

      const response = await fetch(`${API_URL}/login`, {
        method: "POST",
        body: formData,
        credentials: "include", // Include cookies for CSRF protection
        headers: {
          "X-Requested-With": "XMLHttpRequest", // Prevent CSRF
        },
      });

      if (response.ok) {
        const data = await response.json();
        console.log("Login response received:", data);
        if (!data.token) {
          throw new Error("Kein Token erhalten");
        }

        console.log("Token received, length:", data.token.length);
        
        // Store token with expiry
        const expiryTime = Date.now() + TOKEN_EXPIRY;
        localStorage.setItem(TOKEN_KEY, data.token);
        localStorage.setItem(TOKEN_EXPIRY_KEY, expiryTime.toString());
        console.log("Token stored in localStorage with expiry");
        
        // Record successful attempt
        recordLoginAttempt(true);
        
        // Clear login attempts on success
        localStorage.removeItem(LOGIN_ATTEMPTS_KEY);
        
        // Navigate to admin page
        console.log("Navigating to /admin");
        window.location.href = "/admin";
      } else {
        // Record failed attempt
        recordLoginAttempt(false);
        if (response.status === 401) {
          throw new Error("Ungültige Anmeldedaten");
        } else {
          throw new Error("Ein Fehler ist aufgetreten. Bitte versuchen Sie es später erneut.");
        }
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleOIDCLogin = async () => {
    try {
      const response = await fetch(`${API_URL}/auth/login?redirect_uri=/admin`, {
        method: "GET",
        credentials: "include",
      });

      if (response.ok) {
        const data = await response.json();
        // Redirect to the authorization URL returned by the backend
        window.location.href = data.auth_url;
      } else {
        setError("OIDC-Anmeldung nicht verfügbar");
      }
    } catch (err) {
      setError("Fehler bei der OIDC-Anmeldung");
    }
  };

  return (
    <div className="flex flex-col min-h-screen w-screen bg-gray-100 dark:bg-zinc-900">
      <section className="flex-1 flex items-center justify-center">
        <form
          onSubmit={handleSubmit}
          className="bg-white dark:bg-zinc-800 p-8 rounded-xl shadow-lg w-full max-w-sm"
        >
          <h2 className="text-2xl font-bold mb-6 text-center">Login</h2>
          {error && (
            <div className="text-red-500 mb-4 text-center p-2 bg-red-50 dark:bg-red-900/20 rounded">
              {error}
            </div>
          )}
          <div className="mb-4">
            <label className="block mb-1">Benutzername</label>
            <input
              type="text"
              value={username}
              onChange={e => setUsername(e.target.value)}
              className="w-full p-2 border rounded bg-white dark:bg-zinc-900"
              required
              disabled={isLoading}
              autoComplete="username"
              maxLength={50}
              pattern="[a-zA-Z0-9@._-]+"
              title="Nur Buchstaben, Zahlen und @._- sind erlaubt"
            />
          </div>
          <div className="mb-6">
            <label className="block mb-1">Passwort</label>
            <input
              type="password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              className="w-full p-2 border rounded bg-white dark:bg-zinc-900"
              required
              disabled={isLoading}
              autoComplete="current-password"
              minLength={8}
              maxLength={100}
            />
          </div>
          <button
            type="submit"
            className={`w-full bg-black text-white py-2 rounded transition ${
              isLoading ? "opacity-50 cursor-not-allowed" : "hover:bg-gray-800"
            }`}
            disabled={isLoading}
          >
            {isLoading ? "Anmeldung..." : "Login"}
          </button>
          
          <div className="mt-4 text-center">
            <span className="text-gray-600 dark:text-gray-400">oder</span>
          </div>
          
          <button
            type="button"
            onClick={handleOIDCLogin}
            className="w-full mt-4 bg-blue-600 text-white py-2 rounded transition hover:bg-blue-700 flex items-center justify-center gap-2"
          >
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
              <path d="M18.48 9.23c-.98-1.07-2.34-1.73-3.84-1.73-1.97 0-3.75 1.14-4.58 2.91-.34-.06-.68-.09-1.04-.09-2.37 0-4.29 1.92-4.29 4.29s1.92 4.29 4.29 4.29h8.86c1.77 0 3.21-1.44 3.21-3.21 0-1.77-1.44-3.21-3.21-3.21-.14 0-.28.01-.41.03a4.643 4.643 0 00-1-3.28z"/>
            </svg>
            Mit Bitnora ID anmelden
          </button>
        </form>
      </section>
    </div>
  );
}

export default Login; 